import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    Grid,
    Avatar,
    Alert,
    CircularProgress,
    IconButton
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getCompanySettings, updateCompanySettings } from '../../Api/CompanyApi';
import { getActiveIndustries } from '../../Api/IndustryApi';

const CompanySettings = () => {
    const [formData, setFormData] = useState({
        companyName: '',
        gstinNumber: '',
        websiteLink: '',
        industryType: '',
        companySize: ''
    });
    const [companyId, setCompanyId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [industryTypes, setIndustryTypes] = useState([]);
    const [loadingIndustries, setLoadingIndustries] = useState(true);
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    const handleInputChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value
        });
        setError('');
        setSuccess('');
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file.');
                return;
            }
            // Validate file size (e.g., 5MB max)
            if (file.size > 5 * 1024 * 1024) {
                setError('Image size should be less than 5MB.');
                return;
            }
            setLogo(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
            setError('');
            setSuccess('');
        }
    };

    const handleRemoveLogo = (e) => {
        e.stopPropagation(); // Prevent triggering the file input
        setLogo(null);
        setLogoPreview(null);
        // Reset file input
        const fileInput = document.getElementById('logo-upload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    useEffect(() => {
        const fetchIndustries = async () => {
            try {
                setLoadingIndustries(true);
                const response = await getActiveIndustries();
                const industries = response?.data || response || [];
                setIndustryTypes(industries);
            } catch (err) {
                console.error('Failed to fetch industries:', err);
                // Don't show error to user, just log it
            } finally {
                setLoadingIndustries(false);
            }
        };

        fetchIndustries();
    }, []);

    useEffect(() => {
        const fetchCompanySettings = async () => {
            try {
                setLoading(true);
                const response = await getCompanySettings();
                const company =
                    response?.data ||
                    response?.company ||
                    response;

                if (company) {
                    setCompanyId(company.id ?? company.companyId ?? null);
                    // Map industry - could be id or name, try to match with industryTypes
                    const industryValue = company.industryId || company.industry?.id || company.industryType || company.industry?.name || '';
                    setFormData({
                        companyName: company.name || '',
                        gstinNumber: company.gstin || '',
                        websiteLink: company.website || '',
                        industryType: industryValue,
                        companySize: company.companySize || ''
                    });
                    // Set logo preview if logo URL/S3 key exists
                    if (company.logoS3Key || company.logo || company.logoUrl) {
                        setLogoPreview(company.logoS3Key || company.logo || company.logoUrl);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch company settings:', err);
                const message = err.response?.data?.message || err.message || 'Unable to load company settings.';
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        fetchCompanySettings();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!companyId) {
            setError('Company id is missing. Please reload and try again.');
            return;
        }

        // Get industryId from the selected industry
        const selectedIndustry = industryTypes.find(
            (industry) => (industry.id || industry.industryId) === formData.industryType || (industry.name || industry) === formData.industryType
        );
        const industryId = selectedIndustry?.id || selectedIndustry?.industryId || formData.industryType;

        // Use FormData if logo is present, otherwise use JSON
        let payload;
        if (logo) {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.companyName?.trim() || '');
            formDataToSend.append('gstin', formData.gstinNumber?.trim() || '');
            formDataToSend.append('website', formData.websiteLink?.trim() || '');
            formDataToSend.append('industryId', industryId);
            formDataToSend.append('companySize', formData.companySize?.trim() || '');
            formDataToSend.append('logo', logo);
            payload = formDataToSend;
        } else {
            payload = {
                name: formData.companyName?.trim() || '',
                gstin: formData.gstinNumber?.trim() || '',
                website: formData.websiteLink?.trim() || '',
                industryId: industryId,
                companySize: formData.companySize?.trim() || '',
            };
        }

        try {
            setSaving(true);
            const response = await updateCompanySettings(companyId, payload);
            const updatedCompany = response?.data || response?.company || response;
            if (updatedCompany) {
                if (updatedCompany.logoS3Key || updatedCompany.logo || updatedCompany.logoUrl) {
                    setLogoPreview(updatedCompany.logoS3Key || updatedCompany.logo || updatedCompany.logoUrl);
                }
            }
            setLogo(null);
            setSuccess('Company settings updated successfully.');
        } catch (err) {
            console.error('Failed to update company settings:', err);
            const message = err.response?.data?.message || err.message || 'Unable to update company settings.';
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {(loading || saving) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            {error && (
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            <Grid container spacing={4}>
                {/* Left Column - Form Fields */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Company Name */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <BusinessOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                    Company Name
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter company Name"
                                value={formData.companyName}
                                onChange={handleInputChange('companyName')}
                                variant="outlined"
                            />
                        </Box>

                        {/* GSTIN Number */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                    GSTIN Number
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter GST Number"
                                value={formData.gstinNumber}
                                onChange={handleInputChange('gstinNumber')}
                                variant="outlined"
                            />
                        </Box>

                        {/* Website Link */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LanguageOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                    Website Link <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter Company website"
                                value={formData.websiteLink}
                                onChange={handleInputChange('websiteLink')}
                                variant="outlined"
                                required
                            />
                        </Box>

                        {/* Industry Type */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <WorkOutlineOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                    Industry Type <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            </Box>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={formData.industryType}
                                    onChange={handleInputChange('industryType')}
                                    displayEmpty
                                    required
                                    disabled={loadingIndustries || loading}
                                >
                                    <MenuItem value="" disabled>
                                        {loadingIndustries ? 'Loading industries...' : 'Select Your Industry'}
                                    </MenuItem>
                                    {industryTypes.map((industry) => {
                                        const industryId = industry.id || industry.industryId;
                                        const industryName = industry.name || industry;
                                        return (
                                            <MenuItem key={industryId || industryName} value={industryId || industryName}>
                                                {industryName}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Company Size */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PeopleOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '14px' }}>
                                    Company Size <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            </Box>
                             <TextField fullWidth size="small" placeholder="Enter Company Size"
                                value={formData.companySize} onChange={handleInputChange('companySize')}
                                variant="outlined" required
                            />
                        </Box>
                    </Box>
                </Grid>

                {/* Right Column - Logo and Button */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: { xs: 'start', md: 'flex-end' }, justifyContent: 'space-between',
                        minHeight: { xs: 'auto', md: '100%' }, mt: { xs: 2, md: 0 }
                    }}>
                        {/* Logo Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ position: 'relative' }}>
                                <input
                                    id="logo-upload"
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                    disabled={loading || saving}
                                />
                                <Avatar
                                    onClick={() => !loading && !saving && document.getElementById('logo-upload').click()}
                                    src={logoPreview || undefined}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        bgcolor: logoPreview ? 'transparent' : 'white',
                                        border: '1px solid #E5E7EB',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: loading || saving ? 'default' : 'pointer',
                                        '&:hover': {
                                            opacity: loading || saving ? 1 : 0.8
                                        }
                                    }}
                                >
                                    {!logoPreview && (
                                        <>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#3B82F6',
                                                    fontSize: '24px',
                                                    lineHeight: 1,
                                                    '& .red-i': { color: '#EF4444' }
                                                }}
                                            >
                                                H<span className="red-i">I</span>RRAC
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '10px',
                                                    fontWeight: 400
                                                }}
                                            >
                                                Elevating your success
                                            </Typography>
                                        </>
                                    )}
                                </Avatar>
                                {logoPreview && (
                                    <IconButton
                                        onClick={handleRemoveLogo}
                                        disabled={loading || saving}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'white',
                                            border: '1px solid #E5E7EB',
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                                bgcolor: '#F3F4F6',
                                                borderColor: '#D1D5DB'
                                            },
                                            '&:disabled': {
                                                opacity: 0.5
                                            }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px', mt: 1 }}>
                                Logo
                            </Typography>
                        </Box>

                        {/* Save Button */}
                         <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={saving || loading}
                            >
                                {saving ? 'Saving...' : 'Confirm & Save'}
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CompanySettings;

